import {useCallback, useEffect, useReducer, useRef} from 'react';
import {useReactToPrint} from 'react-to-print';
import _ from 'lodash';

import {
  DEFAULT_WATERMARK_TEXT,
  sanitizeWatermarkText,
  injectWatermark,
} from './usePrintDocument.utils';
import {
  getContentWidth,
  getContentHeight,
} from '../pages/report-workspace/V2/pagination';
import {revalidateEditableZoneAutoResizeInIframe} from './usePrintDocumentV2.utils';
// import {injectPrintFonts} from '../context/DocumentStyleProvider';
import {platformKey} from '../util/platform';
import {PAGE_NUMBER_ALIGN_ITEMS_MAP} from '../constants';
import {AlignmentType} from 'docx';

// Watermark SVG/text pipeline: ./usePrintDocument.utils.js (this hook imports it; V2 print
// imports the same module directly). Named exports at the bottom of this file re-export
// watermark helpers for existing callers/tests except sanitizeWatermarkText, which is
// hook-internal only—import it from ./usePrintDocument.utils if needed elsewhere.

// Platform-specific clearance thresholds for print drift correction.
// Windows non-integer DPRs (e.g. 1.5, 1.65) accumulate sub-pixel drift
// in the print pipeline; Mac integer DPR (2.0) has near-zero drift.
const CLEARANCE_THRESHOLDS = {
  'Win+Chrome': 0,
  'Win+Edge': 3,
  'Win+Firefox': 5,
  'Mac+Chrome': 6,
  'Mac+Edge': 3,
  'Mac+Firefox': 3.5,
};

// Inline-block elements (tables, images) are atomic: if they barely
// don't clear a page break, the browser pushes the entire element.
// Give them extra clearance headroom beyond the text threshold.
const INLINE_BLOCK_EXTRA = 5;

// Max shortfall (px) before an inline-block element that barely overflows a
// page break gets pushed back into the previous page instead of forward.
const PUSH_BACK_THRESHOLDS = {
  'Win+Chrome': 8,
  'Win+Edge': 8,
  'Win+Firefox': 50,
  'Mac+Chrome': 8,
  'Mac+Edge': 8,
  'Mac+Firefox': 8,
};

/**
 * Fix z-index stacking so cells with border-bottom always paint on top of
 * the cells directly below them.
 *
 * Problem: @media print gives every non-striped cell background-color:#fff.
 * A cell in row N+1 with the same z-index as a border-bottom cell in row N
 * paints its white background after (on top of) that border, making the border
 * appear thin or invisible at that column position.
 *
 * Fix: for each cell that has border-bottom, find the max z-index of any cell
 * occupying the same column slots in the immediately following row. If that
 * max is >= the border-bottom cell's z-index, raise the border-bottom cell's
 * z-index to max+1 so it paints after (on top of) those cells.
 */
function fixBorderBottomZIndex(iframeDoc) {
  iframeDoc.querySelectorAll('.handsontable table').forEach((table) => {
    const rows = Array.from(table.querySelectorAll('tr'));

    // Build a grid map: grid[row][col] = { cell, startRow }
    const grid = [];
    rows.forEach((tr, rowIdx) => {
      if (!grid[rowIdx]) grid[rowIdx] = [];
      let colIdx = 0;
      Array.from(tr.cells).forEach((cell) => {
        while (grid[rowIdx][colIdx]) colIdx++;
        const rowspan = cell.rowSpan || 1;
        const colspan = cell.colSpan || 1;
        for (let r = rowIdx; r < rowIdx + rowspan; r++) {
          if (!grid[r]) grid[r] = [];
          for (let c = colIdx; c < colIdx + colspan; c++) {
            grid[r][c] = {cell, startRow: rowIdx};
          }
        }
        colIdx += colspan;
      });
    });

    // Process rows bottom-up so that when we raise row N's cell, row N+1's
    // cells have already been raised to their final z-indices. This guarantees
    // a strict stacking order: row N cell > row N+1 cell > row N+2 cell, etc.
    for (let rowIdx = grid.length - 1; rowIdx >= 0; rowIdx--) {
      const row = grid[rowIdx];
      if (!row) continue;
      const seen = new Set();
      row.forEach((entry) => {
        if (!entry || entry.startRow !== rowIdx || seen.has(entry.cell)) return;
        seen.add(entry.cell);

        const td = entry.cell;
        // Use individual longhand properties rather than cssText string
        // matching, because browsers normalize shorthand like
        // "border-bottom: 1pt solid black" into separate longhand properties
        // in cssText, causing the string match to silently fail.
        const bbs = td.style.borderBottomStyle;
        const hasBorderBottom = bbs === 'solid' || bbs === 'double';
        if (!hasBorderBottom) return;

        const cellZ = parseInt(td.style.zIndex) || 0;
        const cellColStart = row.indexOf(entry);
        const colspan = td.colSpan || 1;
        const nextRowIdx = rowIdx + (td.rowSpan || 1);
        const nextRow = grid[nextRowIdx];
        if (!nextRow) return;

        // Find max z-index among cells below this cell's column span
        let maxBelowZ = -Infinity;
        const seenBelow = new Set();
        for (let c = cellColStart; c < cellColStart + colspan; c++) {
          const below = nextRow[c];
          if (!below || seenBelow.has(below.cell)) continue;
          seenBelow.add(below.cell);
          maxBelowZ = Math.max(
            maxBelowZ,
            parseInt(below.cell.style.zIndex) || 0
          );
        }

        if (maxBelowZ >= cellZ) {
          // z-index only takes effect on positioned elements. Data cells
          // often lack position: relative, so we must add it or the
          // z-index change is silently ignored by the browser and the
          // below row's white @media print background still covers this
          // cell's border-bottom.
          if (!td.style.position) td.style.position = 'relative';
          td.style.zIndex = String(maxBelowZ + 1);
        }
      });
    }
  });
}

// Print phases representing the state machine
const PrintPhase = {
  IDLE: 'idle',
  PRINTING: 'printing',
};

// Action types for the reducer
const ActionTypes = {
  BEGIN_PRINT: 'BEGIN_PRINT',
  RESET: 'RESET',
};

// Minimum time (ms) the loading modal must remain visible before dismissing
const MIN_LOADING_MS = 2000;

const initialState = {
  phase: PrintPhase.IDLE,
};

/**
 * Using a reducer to keep state transitions predictable (IDLE → PRINTING → IDLE).
 */
function printReducer(state, action) {
  switch (action.type) {
    case ActionTypes.BEGIN_PRINT:
      return {
        ...state,
        phase: PrintPhase.PRINTING,
      };
    case ActionTypes.RESET:
      return initialState;
    default:
      return state;
  }
}

/**
 * @typedef {Object} UsePrintDocumentOptions
 * @property {Object} documentData - The document data object containing name and other metadata
 * @property {React.RefObject} contentToPrintRef - Ref to the content element to print
 * @property {Function} [onAfterPrint] - Optional callback to run after print completes
 * @property {Function} [onBeforePrint] - Optional callback to run before print starts
 * @property {string} [watermarkText=DEFAULT_WATERMARK_TEXT] - Text to display as watermark.
 *   Use this for simple text watermarks like "DRAFT", "CONFIDENTIAL", etc.
 *   Supports multi-line text using \n (e.g., "PRELIMINARY DRAFT -\nSUBJECT TO CHANGE").
 * @property {string} [watermarkSvg] - Custom SVG for watermark pattern (takes precedence over watermarkText).
 *   Use this for logo watermarks or complex graphics. The SVG is automatically scaled to fit
 *   within the tile dimensions. The scale factor is calculated from the SVG's viewBox attribute:
 *   - If viewBox is present (e.g., viewBox="0 0 100 100"), scale = tileWidth / viewBoxWidth
 *   - If viewBox is missing or unparseable, defaults to 200 (matches default TILE_WIDTH for scale=1)
 *   The viewBox should be properly quoted per SVG spec, though unquoted values are handled as a fallback.
 *   Watermark opacity is automatically applied - design your SVG with solid colors.
 * @property {Object} [watermarkConfig] - Custom watermark layout; fields documented in `usePrintDocument.utils.js` (WatermarkConfig typedef).
 *   Use this to override default tile size, opacity, rotation, etc.
 *   Example: { tileWidth: 400, tileHeight: 400, columns: 2 } for larger, sparser watermarks.
 */

/**
 * @typedef {Object} UsePrintDocumentReturn
 * @property {boolean} showPrintPreparation - Whether to show the print preparation modal
 * @property {Function} beginPrint - Function to start the print process, accepts options object with withWatermark, skipPrintDialog, and onReady
 * @property {Function} endPrint - Function to end/cleanup the print process
 * @property {Function} cancelPrintPreparation - Function to cancel print preparation
 */

/**
 * Custom hook to manage document printing functionality with optional watermark support.
 *
 * @param {UsePrintDocumentOptions} options - Configuration options
 * @returns {UsePrintDocumentReturn} Print state and control functions
 *
 * @example
 * // Basic usage - clean PDF without watermark
 * const { beginPrint } = usePrintDocument({
 *   documentData,
 *   contentToPrintRef,
 * });
 * beginPrint();
 *
 * @example
 * // Draft mode - watermark that deters casual removal
 * const { beginPrint } = usePrintDocument({
 *   documentData,
 *   contentToPrintRef,
 *   watermarkText: 'DRAFT',  // or use default
 * });
 * beginPrint({ withWatermark: true });
 *
 * @example
 * // Custom logo watermark
 * const { beginPrint } = usePrintDocument({
 *   documentData,
 *   contentToPrintRef,
 *   watermarkSvg: '<svg viewBox="0 0 200 200">...</svg>',
 * });
 * beginPrint({ withWatermark: true });
 *
 * @example
 * // Tiled watermark pattern (harder to remove by cropping)
 * // Use the TILED_WATERMARK_CONFIG preset for easy setup
 * import usePrintDocument, { TILED_WATERMARK_CONFIG } from './usePrintDocument';
 *
 * const { beginPrint } = usePrintDocument({
 *   documentData,
 *   contentToPrintRef,
 *   watermarkText: 'DRAFT',
 *   watermarkConfig: TILED_WATERMARK_CONFIG,
 * });
 *
 * @example
 * // Dense watermark pattern (maximum coverage)
 * const { beginPrint } = usePrintDocument({
 *   documentData,
 *   contentToPrintRef,
 *   watermarkConfig: {
 *     tileWidth: 100,   // Very small tiles
 *     tileHeight: 100,
 *     fontSize: 24,     // Small text
 *     columns: 10,      // Many columns
 *     opacity: 0.12,    // More subtle due to density
 *   },
 * });
 *
 * @example
 * // Custom multi-line watermark text (default config shows one large centered watermark)
 * const { beginPrint } = usePrintDocument({
 *   documentData,
 *   contentToPrintRef,
 *   watermarkText: 'CONFIDENTIAL\nDO NOT DISTRIBUTE',
 * });
 *
 * @example
 * // Word download (skip print dialog)
 * beginPrint({ skipPrintDialog: true });
 */
const usePrintDocument = (options = {}) => {
  const {
    documentData,
    contentToPrintRef,
    onAfterPrint,
    onBeforePrint,
    onPrepareContent,
    onPrintError,
    // styleGuide,
    watermarkText = DEFAULT_WATERMARK_TEXT,
    watermarkSvg,
    watermarkConfig,
  } = options;

  // Centralized state using reducer
  const [state, dispatch] = useReducer(printReducer, initialState);

  // Show the preparation modal whenever not idle
  const showPrintPreparation = state.phase !== PrintPhase.IDLE;

  // Refs
  const printTimeoutRef = useRef(null);
  const isPrintProcessActiveRef = useRef(false);
  // Track when beginPrint was called to enforce a minimum loading duration
  const printStartTimeRef = useRef(null);
  // Ref to track if we should skip the print dialog (for Word/PDF downloads)
  const skipPrintDialogRef = useRef(false);
  // Ref to hold print configuration - read by print callback to avoid closure issues
  // This is set in beginPrint and read in the useReactToPrint print callback
  const printConfigRef = useRef({
    shouldWatermark: false,
    watermarkText,
    watermarkSvg,
    watermarkConfig,
  });
  // Ref to store the print function from useReactToPrint.
  // Named distinctly from printConfigRef to clarify purpose:
  // - printConfigRef: holds watermark/print CONFIGURATION (data)
  // - reactToPrintFnRef: holds the print FUNCTION from useReactToPrint (callback)
  // This ref avoids effect dependency on potentially unstable function reference.
  const reactToPrintFnRef = useRef(null);

  // Keep watermark config in sync with props (sanitize e.g. DB-escaped newlines)
  useEffect(() => {
    printConfigRef.current.watermarkText = sanitizeWatermarkText(watermarkText);
    printConfigRef.current.watermarkSvg = watermarkSvg;
    printConfigRef.current.watermarkConfig = watermarkConfig;
  }, [watermarkText, watermarkSvg, watermarkConfig]);

  const endPrint = useCallback(() => {
    // Mark print process as inactive
    isPrintProcessActiveRef.current = false;
    skipPrintDialogRef.current = false;

    // Clear any pending print timeout
    if (printTimeoutRef.current) {
      clearTimeout(printTimeoutRef.current);
      printTimeoutRef.current = null;
    }

    // Reset all state atomically
    dispatch({type: ActionTypes.RESET});
    // Reset print config (keep watermarkText as it comes from props)
    printConfigRef.current.shouldWatermark = false;

    // Call optional callback
    onAfterPrint?.();
  }, [onAfterPrint]);

  const cancelPrintPreparation = useCallback(() => {
    // Mark print process as inactive
    isPrintProcessActiveRef.current = false;
    skipPrintDialogRef.current = false;

    // Clear any pending print timeout
    if (printTimeoutRef.current) {
      clearTimeout(printTimeoutRef.current);
      printTimeoutRef.current = null;
    }

    // Reset all state atomically
    dispatch({type: ActionTypes.RESET});
    // Reset print config (keep watermarkText as it comes from props)
    printConfigRef.current.shouldWatermark = false;

    // Restore normal-view pagination heights in case preparePrint already ran
    onAfterPrint?.();
  }, [onAfterPrint]);

  const handlePrint = useReactToPrint({
    documentTitle: _.get(documentData, 'record.name'),
    removeAfterPrint: true,
    content: () => contentToPrintRef?.current,
    print: async (printIframe) => {
      const iframeDoc =
        printIframe.contentDocument || printIframe.contentWindow.document;

      // Wait for iframe to load
      if (iframeDoc.readyState !== 'complete') {
        await new Promise((resolve) =>
          printIframe.contentWindow.addEventListener('load', resolve)
        );
      }

      // Copy CSS variables from parent to iframe for consistent print styling
      const parentStyles = getComputedStyle(document.documentElement);
      const cssVarsToCopy = [
        ['--document-font-family', '"Times New Roman", Times, serif'],
        ['--page-height', '1056px'],
        ['--page-width', '816px'],
        ['--page-margin-top-bottom', '46px'],
        ['--page-margin-left-right', '46px'],
        ['--line-height', '1.5'],
        ['--list-item-spacing', '0px'],
        ['--widow-orphan-lines', '1'],
      ];
      cssVarsToCopy.forEach(([varName, fallback]) => {
        const value = parentStyles.getPropertyValue(varName) || fallback;
        iframeDoc.documentElement.style.setProperty(varName, value);
      });

      // Resolve page number alignment: document-level setting takes priority,
      // then org-level root CSS variable, then default to center
      const pageNumAlignment =
        documentData?.record?.settings?.pagination?.pageNumberAlignment ||
        documentData?.record?.pageNumberAlignment ||
        parentStyles.getPropertyValue('--page-number-alignment').trim() ||
        AlignmentType.CENTER;
      iframeDoc.documentElement.style.setProperty(
        '--page-number-alignment',
        pageNumAlignment
      );
      iframeDoc.documentElement.style.setProperty(
        '--page-number-align-items',
        PAGE_NUMBER_ALIGN_ITEMS_MAP[pageNumAlignment] ?? AlignmentType.CENTER
      );

      // Override inline font-family styles using CSS instead of DOM iteration
      // This is much more performant for large documents with thousands of elements
      // Exclude .print-immutable elements - these preserve their designed styles in print
      const fontOverrideStyle = iframeDoc.createElement('style');
      fontOverrideStyle.id = 'print-font-override';
      fontOverrideStyle.textContent =
        '*:not(.print-immutable):not(.print-immutable *) { font-family: var(--document-font-family) !important; }';
      iframeDoc.head.appendChild(fontOverrideStyle);

      // No-print pattern for export exclusion:
      //   - className="no-print": hide element entirely (display: none)
      //   - data-no-print="outline": remove outline style only (element stays visible)
      //   - Future: data-no-print="border", "background", "shadow", etc.
      const noPrintStyle = iframeDoc.createElement('style');
      noPrintStyle.id = 'print-no-print-hide';
      noPrintStyle.textContent = `
        .no-print { display: none !important; }
        [data-no-print="outline"] { outline: none !important; }
      `;
      iframeDoc.head.appendChild(noPrintStyle);

      const borderEnhanceStyle = iframeDoc.createElement('style');
      borderEnhanceStyle.id = 'print-border-enhance';
      borderEnhanceStyle.textContent = `
        @media print {
          /* Force exact color rendering and crisp edges */
          .handsontable td {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            image-rendering: crisp-edges !important;
            shape-rendering: crispEdges !important;
          }

          .handsontable td[style*="border-top: 1pt solid"] {
            border-top-color: #0A0A0A !important;
          }

          .handsontable td[style*="border-bottom: 1pt solid"] {
            border-bottom-color: #0A0A0A !important;
          }

          .handsontable td[style*="border-left: 1pt solid"] {
            border-left-color: #0A0A0A !important;
          }

          .handsontable td[style*="border-right: 1pt solid"] {
            border-right-color: #0A0A0A !important;
          }

          .handsontable td[style*="border-top: 3pt double"],
          .handsontable td[style*="border-top-style: double"] {
            border-top-color: #0A0A0A !important;
          }

          .handsontable td[style*="border-bottom: 3pt double"],
          .handsontable td[style*="border-bottom-style: double"] {
            border-bottom-color: #0A0A0A !important;
          }
        }
      `;
      iframeDoc.head.appendChild(borderEnhanceStyle);

      // Inject watermark if enabled (read from ref to get current config)
      const {shouldWatermark, watermarkText, watermarkSvg, watermarkConfig} =
        printConfigRef.current;

      if (shouldWatermark) {
        const contentWidth = getContentWidth();
        const contentHeight = getContentHeight();
        injectWatermark(iframeDoc, {
          text: watermarkText,
          svg: watermarkSvg,
          config: watermarkConfig,
          contentWidth,
          contentHeight,
        });
      }

      // Temporarily set the main window title to the document name so the browser
      // uses it as the PDF filename. Chrome reads document.title from the parent
      // window even when printing via iframe.contentWindow.print().
      const documentName = _.get(documentData, 'record.name');
      const originalTitle = document.title;
      if (documentName) {
        document.title = documentName;
        iframeDoc.title = documentName;
      }

      // Fix z-index stacking so border-bottom cells paint on top of cells below them
      // (which have white backgrounds in print, obscuring the border at same z-index positions)
      fixBorderBottomZIndex(iframeDoc);

      // Inject and load the correct webfont for the active style guide.
      // See DocumentStyleProvider.utils.js for why this is needed.
      // await injectPrintFonts(iframeDoc, styleGuide);

      // Allow browser to reflow with the loaded fonts before printing
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Constrain the iframe viewport to the @page width so that the
      // screen-context layout matches what Chrome's print engine will use.
      // Setting the iframe ELEMENT width (not html/body) changes the actual
      // viewport width.  Without this the iframe is full-monitor-width
      // (e.g. 1728px) but Chrome re-renders at 816px during print.
      printIframe.style.width = '816px';
      // eslint-disable-next-line no-unused-expressions
      iframeDoc.documentElement.offsetHeight;

      // Extract ALL @media print CSS rules from the iframe's stylesheets and
      // re-inject them as unconditional rules.  This makes the iframe render
      // with the same layout Chrome's print engine will use — including
      // hidden .page-break-middle, adjusted line-height/font-size, forced
      // content-visibility, etc.  Without this, the screen-context layout
      // has 15px-per-page drift from visible middles and potentially
      // different text heights, which Chrome would only fix at print() time
      // when the DOM can no longer be measured.
      const printRules = [];
      for (const sheet of iframeDoc.styleSheets) {
        try {
          for (const rule of sheet.cssRules) {
            if (
              rule.type === CSSRule.MEDIA_RULE &&
              rule.media?.mediaText === 'print'
            ) {
              for (const inner of rule.cssRules) {
                printRules.push(inner.cssText);
              }
            }
          }
        } catch (_e) {
          /* cross-origin sheets — skip */
        }
      }
      if (printRules.length > 0) {
        const printStyle = iframeDoc.createElement('style');
        printStyle.textContent = printRules.join('\n');
        iframeDoc.head.appendChild(printStyle);
      }

      // Neutralise break directives and fix print-specific layout issues.
      // IMPORTANT: Force zoom:1 and --zoom-compensation:1 in screen mode
      // so the iframe renders text identically to Chrome's print engine
      // (which always uses @media print { zoom:1 }). Without this, the
      // iframe on Windows shows content at zoom:1.212 (DPR compensation)
      // while the print engine sees zoom:1 — glyph widths differ at the
      // two effective DPRs, causing paragraphs to gain/lose a line and
      // shift content across page boundaries.
      const neutraliseStyle = iframeDoc.createElement('style');
      neutraliseStyle.textContent = [
        '.page-break-top { page-break-after: auto !important; break-after: auto !important; }',
        '.page-break-bottom { page-break-inside: auto !important; break-inside: auto !important; }',
        '.keep-with-next-block { page-break-inside: auto !important; break-inside: auto !important; }',
        // Ensure all sections are fully rendered for print
        '.document-section { content-visibility: visible !important; contain-intrinsic-block-size: none !important; }',
        '.page-break-middle { display: none !important; }',
        // Remove the scrollbar from the print iframe so the body width
        // matches the iframe element width (816px) on all platforms.
        // Windows Chrome uses classic scrollbars (~23px) which shrinks
        // the body to ~793px, causing text to reflow at a narrower width
        // and paragraphs to be taller — accumulating into page-break drift.
        'html, html * { scrollbar-width: none !important; }',
        'html::-webkit-scrollbar, html *::-webkit-scrollbar { display: none !important; }',
        // Strip DPR zoom compensation so the iframe renders at zoom:1,
        // matching Chrome's @media print { zoom: 1 } on sections.
        '.tiptap-container { --zoom-compensation: 1 !important; }',
        '.document-section { zoom: 1 !important; }',
        // Landscape sections are single-page. The pre-applied print CSS
        // forces height:816px but the .page spacer still carries a portrait-
        // sized margin-top (~960px), pushing content outside the section
        // bounds where overflow:hidden clips it.
        '.document-section.landscape .page { margin-top: 0 !important; height: 0 !important; }',
      ].join('\n');
      iframeDoc.head.appendChild(neutraliseStyle);

      // Force synchronous reflow so the print-equivalent layout settles
      // eslint-disable-next-line no-unused-expressions
      iframeDoc.documentElement.offsetHeight;
      const iWin = printIframe.contentWindow;

      // Re-validate editable-zone fit inside the print iframe.
      // Even when the editor resolved a fitted font size, print reflow can differ
      // slightly on some browsers and push one extra wrapped line.
      revalidateEditableZoneAutoResizeInIframe(iframeDoc, iWin);

      // BROKEN IMAGE NORMALIZATION (print iframe side)
      //
      // Browsers render failed-to-load <img> elements at wildly different sizes:
      //   - Chrome:  ~18×18px  (small broken-image icon)
      //   - Firefox: expands to width/height attrs or browser default (~300×150px)
      //   - Safari:  small icon, varies by version
      //
      // Without normalization, a broken image inflates heights in Firefox,
      // shifting all downstream page breaks and producing a different page
      // count than Chrome for the same document. Runs before the clearance/
      // straddle measurement pass so those calculations see consistent sizes.
      //
      // Detection: img.complete && img.naturalWidth === 0 means the browser
      // finished the load attempt and decoded zero image data.
      //
      // The same normalization runs in the viewport (DocumentEditor.jsx) via
      // event delegation so the editor and PDF output stay in sync.
      iframeDoc.querySelectorAll('img').forEach((img) => {
        if (img.classList.contains('ProseMirror-separator')) {
          img.style.setProperty('display', 'none', 'important');
          return;
        }
        if (img.complete && img.naturalWidth === 0) {
          img.style.setProperty('width', '18px', 'important');
          img.style.setProperty('height', '18px', 'important');
          img.style.setProperty('object-fit', 'contain', 'important');
          const wrapper = img.closest('.tiptapV3 > div, .tiptap > div');
          if (wrapper) {
            wrapper.style.setProperty('display', 'block', 'important');
            wrapper.style.setProperty('width', '100%', 'important');
            wrapper.style.setProperty('text-align', 'center', 'important');
          }
          img.style.setProperty('display', 'inline-block', 'important');
        }
      });
      // eslint-disable-next-line no-unused-expressions
      iframeDoc.documentElement.offsetHeight;

      const CLEARANCE_THRESHOLD = CLEARANCE_THRESHOLDS[platformKey] ?? 0;
      let didFixAny = false;

      // Decouple footer visuals from .page-break-top height.
      // The clearance and straddle-micro fixes shrink .page-break-top
      // to give more room to document content above it.  Without this,
      // the page number and footer text inside get clipped.  By moving
      // the children into an absolutely-positioned overlay that keeps
      // the original height, the layout shrink works while the footer
      // content stays visually intact.
      iframeDoc.querySelectorAll('.page-break-top').forEach((pbTop) => {
        if (pbTop.closest('.document-section.landscape')) return;
        const origH = pbTop.getBoundingClientRect().height;
        const cs = iWin.getComputedStyle(pbTop);
        const overlay = iframeDoc.createElement('div');
        overlay.style.cssText =
          'position:absolute;bottom:0;left:0;right:0;' +
          'height:' +
          origH +
          'px;' +
          'z-index:1;' +
          'display:flex;flex-direction:column;justify-content:' +
          cs.justifyContent +
          ';' +
          'padding-top:' +
          cs.paddingTop +
          ';' +
          'box-sizing:border-box;' +
          'background:#fff;';
        while (pbTop.firstChild) overlay.appendChild(pbTop.firstChild);
        pbTop.appendChild(overlay);
        pbTop.style.position = 'relative';
        pbTop.style.overflow = 'visible';
      });

      if (CLEARANCE_THRESHOLD > 0 || INLINE_BLOCK_EXTRA > 0) {
        const sections = iframeDoc.querySelectorAll('.document-section');
        sections.forEach((sec) => {
          const secRect = sec.getBoundingClientRect();
          const pageRef = sec.children[0];
          const tiptap = sec.querySelector('.tiptap');
          if (!pageRef || !tiptap || !tiptap.children.length) return;

          const contentEntries = [];
          for (let c = 0; c < tiptap.children.length; c++) {
            const el = tiptap.children[c];
            contentEntries.push({
              bot: el.getBoundingClientRect().bottom - secRect.top,
              isInlineBlock:
                iWin.getComputedStyle(el).display === 'inline-block',
              idx: c,
            });
          }
          contentEntries.sort((a, b) => a.bot - b.bot);

          const pageBreaks = pageRef.querySelectorAll('.page-break');

          pageBreaks.forEach((pb, pbIdx) => {
            const floatTop = pb.getBoundingClientRect().top - secRect.top;

            let lo = 0;
            let hi = contentEntries.length - 1;
            let best = -1;
            while (lo <= hi) {
              const mid = (lo + hi) >> 1;
              if (contentEntries[mid].bot <= floatTop) {
                best = mid;
                lo = mid + 1;
              } else {
                hi = mid - 1;
              }
            }
            if (best === -1) return;

            const bestEntry = contentEntries[best];
            const clearance = floatTop - bestEntry.bot;
            const effectiveThreshold = bestEntry.isInlineBlock
              ? CLEARANCE_THRESHOLD + INLINE_BLOCK_EXTRA
              : CLEARANCE_THRESHOLD;
            if (clearance > 0 && clearance < effectiveThreshold) {
              const needed =
                Math.ceil((effectiveThreshold - clearance) * 100) / 100;

              const pbTop = pb.querySelector('.page-break-top');
              if (pbTop) {
                const curH = pbTop.getBoundingClientRect().height;
                pbTop.style.height = curH - needed + 'px';
              }

              const spacer = pb.previousElementSibling;
              if (spacer && spacer.classList.contains('page')) {
                const curMT = parseFloat(
                  iWin.getComputedStyle(spacer).marginTop
                );
                spacer.style.marginTop = curMT + needed + 'px';
              }

              didFixAny = true;
            }

            // Micro-expand for straddling text: when a text element
            // straddles a page break with less than one line above,
            // expand the page boundary by a tiny fixed amount (1px).
            // This is NOT enough to pull a new text line to the current
            // page (gap + 1 < lineH), but the cumulative 1px shifts
            // across multiple page breaks cascade downstream, keeping
            // later page boundaries correctly aligned.
            for (let c = 0; c < tiptap.children.length; c++) {
              const elRect = tiptap.children[c].getBoundingClientRect();
              const elTop = elRect.top - secRect.top;
              const elBot = elRect.bottom - secRect.top;

              if (elTop >= floatTop || elBot <= floatTop) continue;

              const gap = floatTop - elTop;
              if (gap <= 0) continue;

              let lineH = parseFloat(
                iWin.getComputedStyle(tiptap.children[c]).lineHeight
              );
              if (isNaN(lineH)) {
                const fs =
                  parseFloat(
                    iWin.getComputedStyle(tiptap.children[c]).fontSize
                  ) || 12;
                lineH = Math.ceil(fs * 1.4);
              }

              const shortfall = lineH - gap;
              if (shortfall > 0) {
                const needed = 1;

                const pbTopEl = pb.querySelector('.page-break-top');
                if (pbTopEl) {
                  const curH = pbTopEl.getBoundingClientRect().height;
                  pbTopEl.style.height = Math.max(curH - needed, 0) + 'px';
                }

                const spacer = pb.previousElementSibling;
                if (spacer && spacer.classList.contains('page')) {
                  const curMT = parseFloat(
                    iWin.getComputedStyle(spacer).marginTop
                  );
                  spacer.style.marginTop = curMT + needed + 'px';
                }

                didFixAny = true;
                break;
              }
            }
          });
        });

        if (didFixAny) {
          // eslint-disable-next-line no-unused-expressions
          iframeDoc.documentElement.offsetHeight;
        }
      }

      // Push-back fix: inline-block elements (tables, images) are atomic — if
      // they don't fit in the remaining space before a page break float, they
      // wrap entirely to the next page.  When the shortfall is tiny (a few px
      // of viewport-to-print rendering drift), we reclaim space by expanding
      // the content area of the affected page (increase spacer margin-top,
      // decrease footer height by the same amount → total cycle stays 1056px).
      {
        const PUSH_BACK_THRESHOLD = PUSH_BACK_THRESHOLDS[platformKey] ?? 6;
        const pushSections = iframeDoc.querySelectorAll('.document-section');
        let pushFixCount = 0;

        pushSections.forEach((sec, _secIdx) => {
          const secRect = sec.getBoundingClientRect();
          const pageRef = sec.children[0];
          const tiptap = sec.querySelector('.tiptap');
          if (!pageRef || !tiptap) return;
          const pbs = Array.from(pageRef.querySelectorAll('.page-break'));

          pbs.forEach((pb, pbIdx) => {
            const pbRect = pb.getBoundingClientRect();
            const pbBot = pbRect.bottom - secRect.top;
            const pbTopPos = pbRect.top - secRect.top;

            for (let c = 0; c < tiptap.children.length; c++) {
              const el = tiptap.children[c];
              const elRect = el.getBoundingClientRect();
              const elTop = elRect.top - secRect.top;
              const elH = elRect.height;

              if (Math.abs(elTop - pbBot) > 1) continue;

              const display = iWin.getComputedStyle(el).display;
              if (display !== 'inline-block') continue;

              let lastContentBot = 0;
              for (let cc = 0; cc < tiptap.children.length; cc++) {
                const ccBot =
                  tiptap.children[cc].getBoundingClientRect().bottom -
                  secRect.top;
                if (ccBot <= pbTopPos + 0.5 && ccBot > lastContentBot) {
                  lastContentBot = ccBot;
                }
              }

              const available = pbTopPos - lastContentBot;
              const shortfall = elH - available;

              if (shortfall > 0 && shortfall < PUSH_BACK_THRESHOLD) {
                const pushBuffer = 5;
                const adjustment =
                  Math.ceil((shortfall + pushBuffer) * 100) / 100;

                const spacer = pb.previousElementSibling;
                if (spacer && spacer.classList.contains('page')) {
                  const curMT = parseFloat(
                    iWin.getComputedStyle(spacer).marginTop
                  );
                  spacer.style.marginTop = curMT + adjustment + 'px';
                }

                const pbTopEl = pb.querySelector('.page-break-top');
                if (pbTopEl) {
                  const curH = pbTopEl.getBoundingClientRect().height;
                  pbTopEl.style.height = Math.max(curH - adjustment, 0) + 'px';
                }

                pushFixCount++;
              }
            }
          });
        });
        if (pushFixCount > 0) {
          // eslint-disable-next-line no-unused-expressions
          iframeDoc.documentElement.offsetHeight;
        }
      }

      printIframe.contentWindow.print();

      // Restore original title after print dialog opens
      if (documentName) {
        document.title = originalTitle;
      }
    },
    onAfterPrint: endPrint,
    bodyClass: document.body.className,
  });

  // Keep ref in sync - useReactToPrint may return unstable reference due to inline functions
  reactToPrintFnRef.current = handlePrint;

  /**
   * Begin the print process
   *
   * @param {Object} [options={}] - Print options
   * @param {boolean} [options.withWatermark=false] - Include watermark on printed document.
   * @param {boolean} [options.skipPrintDialog=false] - Skip browser print dialog (e.g. Word export).
   * @param {Function} [options.onReady] - Called after the brief settle delay instead of opening
   *   the print dialog. Use this for Word/PDF downloads to signal when content is ready.
   */
  const beginPrint = useCallback(
    (options = {}) => {
      const {withWatermark = false, skipPrintDialog = false, onReady} = options;

      isPrintProcessActiveRef.current = true;
      skipPrintDialogRef.current = skipPrintDialog;
      printConfigRef.current.shouldWatermark = withWatermark;
      printStartTimeRef.current = Date.now();

      // Show the modal
      dispatch({type: ActionTypes.BEGIN_PRINT});

      onBeforePrint?.();

      // Brief delay to let the modal render, then adjust pagination for print
      // (removes page-divider pixels from section heights) and trigger the clone.
      // All DOM work happens here — zero React re-renders in the document tree.
      printTimeoutRef.current = setTimeout(async () => {
        printTimeoutRef.current = null;
        if (!isPrintProcessActiveRef.current) return;

        try {
          // Run print-mode pagination (pure DOM mutations) before cloning
          if (onPrepareContent) {
            await onPrepareContent();
          }
        } catch (error) {
          console.error('Error during print preparation:', error);
          isPrintProcessActiveRef.current = false;
          dispatch({type: ActionTypes.RESET});
          printConfigRef.current.shouldWatermark = false;
          onPrintError?.(error);
          return;
        }

        if (!isPrintProcessActiveRef.current) return; // guard in case cancelled during prepare

        // Enforce minimum loading duration so the modal is never a brief flash
        const elapsed = Date.now() - (printStartTimeRef.current ?? Date.now());
        const remaining = MIN_LOADING_MS - elapsed;
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }

        if (!isPrintProcessActiveRef.current) return; // guard in case cancelled during wait

        if (skipPrintDialog) {
          // For Word/PDF export: notify caller that content is ready
          onReady?.();
        } else if (contentToPrintRef?.current) {
          reactToPrintFnRef.current?.();
          isPrintProcessActiveRef.current = false;
          dispatch({type: ActionTypes.RESET});
        } else {
          console.error('Print content ref not available');
          endPrint();
        }
      }, 100);
    },
    [onBeforePrint, onPrepareContent, onPrintError, endPrint]
  );

  return {
    // State flags - use these to conditionally render UI
    showPrintPreparation, // True when preparation modal should be visible

    // Functions
    beginPrint, // Start print process (optionally with watermark)
    endPrint, // Clean up after print (called automatically by onAfterPrint)
    cancelPrintPreparation, // User cancelled - abort and reset
  };
};

export default usePrintDocument;

/**
 * Named exports for advanced customization and testing (implemented in
 * `usePrintDocument.utils.js`):
 * - WATERMARK_CONFIG: Default config (single large centered watermark)
 * - TILED_WATERMARK_CONFIG: Preset for tiled/repeating watermark pattern
 * - DEFAULT_WATERMARK_TEXT: The default multi-line watermark text
 * - injectWatermark: Low-level function to inject watermark into iframe
 * - generateTextWatermarkSvg: Generate SVG pattern from text (useful as template for custom SVGs)
 *
 * For `sanitizeWatermarkText`, import from `./usePrintDocument.utils` (not re-exported here).
 */
export {
  WATERMARK_CONFIG,
  TILED_WATERMARK_CONFIG,
  DEFAULT_WATERMARK_TEXT,
  injectWatermark,
  generateTextWatermarkSvg,
} from './usePrintDocument.utils';
